import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Camera, Loader2, ChevronDown, ChevronUp, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MessageActionSheet, LongPressBubble, ConversationBlockMenu } from "@/components/MessageActions";
import { authFileUrl } from "@/lib/api";


export default function ChatPanel({ requestId, customerName }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [actionMsg, setActionMsg] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await API.get(`/chat/${requestId}/messages`);
      setMessages(data.messages);
    } catch (err) { console.error(err); }
  }, [requestId]);

  useEffect(() => {
    if (open && requestId) {
      fetchMessages();
      const i = setInterval(fetchMessages, 5000);
      return () => clearInterval(i);
    }
  }, [open, requestId, fetchMessages]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await API.post(`/chat/${requestId}/messages`, { text: text.trim(), sender_type: "technician" });
      setText("");
      fetchMessages();
    } catch (err) { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const sendPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSending(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("caption", "");
    try {
      await API.post(`/chat/${requestId}/messages/photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 30000,
      });
      fetchMessages();
    } catch (err) { toast.error("Failed to send photo"); }
    finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const isMine = (msg) => (msg.sender_user_id ? msg.sender_user_id === user?.id : msg.sender_type === "technician");
  const moderate = (kind, body) => API.post(`/moderation/${kind}`, body).catch((e) => {
    throw new Error(e.response?.data?.detail || "Something went wrong");
  });

  return (
    <div className="space-y-2" data-testid="chat-panel">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-2.5 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
        data-testid="chat-toggle">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-semibold text-blue-800">
            Chat with {customerName || "Homeowner"}
          </span>
          {messages.length > 0 && (
            <span className="text-[10px] bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full font-bold">
              {messages.length}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-blue-500" /> : <ChevronDown className="w-4 h-4 text-blue-500" />}
      </button>

      {open && (
        <div className="border border-blue-200 rounded-lg overflow-hidden bg-white" data-testid="chat-open">
          {/* Share link */}
          <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <span className="text-[10px] text-blue-600">Share chat with homeowner:</span>
            <ConversationBlockMenu messages={messages} isMine={isMine} post={moderate} onChanged={fetchMessages} />
            <button onClick={async () => {
              try {
                const { data } = await API.get(`/requests/${requestId}/homeowner-link`);
                await navigator.clipboard.writeText(`${window.location.origin}/chat/${requestId}?t=${data.token}`);
                toast.success("Chat link copied!");
              } catch { toast.error("Could not create the homeowner link"); }
            }} className="text-[10px] text-blue-700 font-bold flex items-center gap-1 hover:underline"
              data-testid="share-chat-link">
              <Link2 className="w-3 h-3" /> Copy Link
            </button>
          </div>
          {/* Messages */}
          <div className="h-52 overflow-y-auto p-3 space-y-2 bg-zinc-50">
            {messages.length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-8">No messages yet. Start the conversation.</p>
            )}
            <p className="text-[9px] text-zinc-400 text-center" data-testid="chat-safety-note">
              Offensive language is filtered. Long-press a message to report or block the sender.
            </p>
            {messages.map(msg => {
              const isMe = isMine(msg);
              return (
                <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                  <LongPressBubble onLongPress={() => !isMe && setActionMsg(msg)} className={`max-w-[80%] px-3 py-2 rounded-xl text-xs select-none ${
                    isMe
                      ? "bg-zinc-900 text-white rounded-br-sm"
                      : msg.sender_type === "management"
                        ? "bg-blue-100 text-blue-900 rounded-bl-sm"
                        : "bg-white text-zinc-800 border border-zinc-200 rounded-bl-sm"
                  }`} data-testid={`chat-msg-${msg.id}`}>
                    {!isMe && (
                      <p className="text-[9px] font-bold opacity-70 mb-0.5">{msg.sender_name}</p>
                    )}
                    {msg.message_type === "photo" && msg.file_url && (
                      <img src={authFileUrl(msg.file_url)} alt="Photo"
                        className="w-full max-w-[200px] rounded-lg mb-1" loading="lazy" />
                    )}
                    {msg.text && <p className="leading-relaxed">{msg.text}</p>}
                    <p className={`text-[9px] mt-1 ${isMe ? "text-zinc-400" : "text-zinc-400"}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </LongPressBubble>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-2 border-t border-zinc-200 flex gap-2 items-end">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={sendPhoto} />
            <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}
              disabled={sending} className="h-8 w-8 p-0 flex-shrink-0"
              data-testid="chat-photo-btn">
              <Camera className="w-4 h-4 text-zinc-400" />
            </Button>
            <input
              type="text" value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1 px-3 py-1.5 border border-zinc-200 rounded-full text-xs focus:outline-none focus:border-blue-400"
              data-testid="chat-input"
            />
            <Button size="sm" onClick={sendMessage} disabled={sending || !text.trim()}
              className="h-8 w-8 p-0 bg-blue-600 hover:bg-blue-700 rounded-full flex-shrink-0"
              data-testid="chat-send-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : <Send className="w-3.5 h-3.5 text-white" />}
            </Button>
          </div>
        </div>
      )}
      <MessageActionSheet msg={actionMsg} onClose={() => setActionMsg(null)} post={moderate} onChanged={fetchMessages} />
    </div>
  );
}
